import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Skeleton,
  Button,
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface EducationLevelData {
  level_name: string;
  level_id: number;
  schools: {
    total_schools: number;
    government_schools: number;
    private_schools: number;
  };
  enrollment: {
    total_enrolled: number;
    female_enrolled: number;
    male_enrolled: number;
    gender_parity_index: number;
  };
  completion_and_dropout: {
    dropout_rate: number;
    completion_rate: number;
    retention_rate: number;
  };
}

interface ReportData {
  academic_year: number;
  education_levels: Record<string, EducationLevelData>;
  summary: {
    total_schools: number;
    total_students: number;
    overall_gender_parity_index: number;
  };
}

const EducationLevelSummary: React.FC = () => {
  const navigate = useNavigate();

  const { data: reportData, isLoading, error } = useQuery<{ data: ReportData }>({
    queryKey: ['education-level-summary'],
    queryFn: async () => {
      const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
      const response = await axios.get(
        `${API_URL}/sis-integration/education-level-reports/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !reportData) {
    return (
      <Alert severity="error">
        Failed to load education level data. Please check your connection.
      </Alert>
    );
  }

  const report = reportData.data;

  // Focus on main education levels: Pre-Primary, Primary, Secondary
  const mainLevels = ['Pre-Primary', 'Primary', 'Secondary'];
  const displayLevels = Object.entries(report.education_levels)
    .filter(([levelName]) => mainLevels.includes(levelName))
    .sort(([a], [b]) => mainLevels.indexOf(a) - mainLevels.indexOf(b));

  const getRiskColor = (rate: number) => {
    if (rate > 15) return 'error';
    if (rate > 10) return 'warning';
    return 'success';
  };

  const getGenderParityColor = (index: number) => {
    if (index >= 0.9 && index <= 1.1) return 'success';
    if (index >= 0.8 && index <= 1.2) return 'warning';
    return 'error';
  };

  return (
    <Box>
      {/* Header with View Full Report Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Education Level Overview
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          onClick={() => navigate('/reports/education-levels')}
        >
          View Full Report
        </Button>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Card variant="outlined" sx={{ textAlign: 'center' }}>
            <CardContent sx={{ pb: '8px !important' }}>
              <PeopleIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Total Students
              </Typography>
              <Typography variant="h6">
                {report.summary.total_students.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card variant="outlined" sx={{ textAlign: 'center' }}>
            <CardContent sx={{ pb: '8px !important' }}>
              <SchoolIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Total Schools
              </Typography>
              <Typography variant="h6">
                {report.summary.total_schools.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card variant="outlined" sx={{ textAlign: 'center' }}>
            <CardContent sx={{ pb: '8px !important' }}>
              <TrendingUpIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Overall GPI
              </Typography>
              <Typography variant="h6">
                {report.summary.overall_gender_parity_index}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Education Level Cards */}
      <Grid container spacing={2}>
        {displayLevels.map(([levelName, levelData]) => (
          <Grid item xs={12} md={4} key={levelName}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {levelName}
                </Typography>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Students: {levelData.enrollment.total_enrolled.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schools: {levelData.schools.total_schools}
                  </Typography>
                </Box>

                {/* Gender Parity */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Gender Parity Index
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={levelData.enrollment.gender_parity_index}
                      size="small"
                      color={getGenderParityColor(levelData.enrollment.gender_parity_index)}
                    />
                  </Box>
                </Box>

                {/* Dropout Rate */}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Dropout Rate: {levelData.completion_and_dropout.dropout_rate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(levelData.completion_and_dropout.dropout_rate, 100)}
                    color={getRiskColor(levelData.completion_and_dropout.dropout_rate)}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                {/* Completion Rate */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Completion Rate: {levelData.completion_and_dropout.completion_rate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={levelData.completion_and_dropout.completion_rate}
                    color="success"
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Data Source Info */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Academic Year {report.academic_year} • Real SIS Data • Last Updated: {new Date().toLocaleDateString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default EducationLevelSummary;